import { PublicClientApplication } from "@azure/msal-browser";
import { useMsal } from "@azure/msal-react";
import { makeStyles, mergeClasses, Spinner } from "@fluentui/react-components";
import React, { useContext, useEffect, useState } from "react";
import { NoDataCard } from "../../../components-v2/common/no-data-card/NoDataCard";
import { NotificationContext } from "../../../context/NotificationContext";
import DEFAULT_DIMENSIONS_DATA from "../../../data/app-settings/dimensions.json";
import { SelectedDimensionTabEnum } from "../../../enum/SelectedDimensionTabEnum";
import { useApiCall } from "../../../hooks/useApiCall";
import IDimension from "../../../models/admin/IDimension";
import { AdminHelper } from "../../../utils/admin/AdminHelper";
import { transformHopSettingsData } from "../../../utils/app-settings/AppSettingsHelper";
import { scrollToTop } from "../../../utils/common/CommonHelperFunctions";
import {
  AIRPORT_CODE_PARAMETER,
  EMPTY_STATE_MESSAGE,
  MESSAGEBAR_TYPE,
} from "../../../utils/common/Constants";
import { DimensionDetails } from "./DimensionDetails";
import { DimensionTabs } from "./DimensionTabs";

const dimensionData = DEFAULT_DIMENSIONS_DATA;

const hopSettingsURL = process.env.REACT_APP_API_GET_HOP_SETTINGS;
const baseURL = process.env.REACT_APP_API_BASE_URL;
const url = `${baseURL}${hopSettingsURL}`;

interface IHopSettingsProps {
  airportCode: string;
}

const HopSettings: React.FC<IHopSettingsProps> = ({ airportCode }) => {
  const msalInstance = useMsal().instance as PublicClientApplication;
  const classes = useStyles();
  const { addNotification } = useContext(NotificationContext);

  const [selectedDimensionTab, setSelectedDimensionTab] = useState<string>(
    SelectedDimensionTabEnum.OTP
  );
  const [savedDimensionsMap, setSavedDimensionsMap] = useState<
    Record<string, IDimension>
  >({});

  const [selectedDimensionDetails, setSelectedDimensionDetails] = useState<
    IDimension | undefined
  >(undefined);

  const dimensionTabList =
    dimensionData?.dimensions?.map((d: IDimension) => {
      return {
        value: d.name,
        text: d.label,
      };
    }) || [];

  //todo: Temporary code to filter out the runway delay tab for YQB
  const hopSettingsTabs =
    airportCode === "YQB"
      ? dimensionTabList.filter((tab) => tab.value !== "runwayDelay")
      : dimensionTabList;

  const hopUrls = dimensionTabList.map((tab) =>
    url
      .replace(AIRPORT_CODE_PARAMETER, airportCode)
      .replace("{kpiType}", tab.value)
  );

  const {
    data: hopSettingsData,
    error,
    isLoading,
  } = useApiCall<any>({ url: hopUrls, dependencies: [airportCode] });

  useEffect(() => {
    if (!hopSettingsData) return;
    const updatedDimensions = [...dimensionData.dimensions];
    transformHopSettingsData(
      { dimensions: updatedDimensions },
      hopSettingsData.map((d: any) => d.data)
    );
    const newSavedMap: Record<string, IDimension> = {};
    updatedDimensions.forEach((dim) => {
      newSavedMap[dim.name] = dim;
    });
    setSavedDimensionsMap(newSavedMap);
    setSelectedDimensionDetails(newSavedMap[selectedDimensionTab]);
  }, [hopSettingsData]);

  const onDimensionTabChange = (selectedType: SelectedDimensionTabEnum) => {
    setSelectedDimensionTab(selectedType);
    if (savedDimensionsMap[selectedType]) {
      setSelectedDimensionDetails(savedDimensionsMap[selectedType]);
    } else {
      // fallback
      const fallbackDim = dimensionData.dimensions.find(
        (d) => d.name === selectedType
      );
      setSelectedDimensionDetails(fallbackDim);
    }
  };

  const onChangeThresholdValue = (newDimensionDetails: IDimension) => {
    setSelectedDimensionDetails(newDimensionDetails);
  };

  const onCancelDimension = () => {
    if (selectedDimensionTab && savedDimensionsMap[selectedDimensionTab]) {
      setSelectedDimensionDetails(savedDimensionsMap[selectedDimensionTab]);
    }
  };

  const onSaveDimension = (newDimensionDetails: IDimension) => {
    const payload = AdminHelper.getSaveDimensionPayload(newDimensionDetails);
    AdminHelper.saveThresholdByDimension(
      msalInstance,
      newDimensionDetails.name,
      payload,
      airportCode
    )
      .then((response: any) => {
        if (response && response.status === 200) {
          addNotification(
            MESSAGEBAR_TYPE.WARNING,
            "Reload all open APOC tabs to apply changes"
          );
          addNotification(
            MESSAGEBAR_TYPE.SUCCESS,
            `Health Of Operations settings saved successfully`
          );
          setSavedDimensionsMap((prev) => ({
            ...prev,
            [newDimensionDetails.name]: newDimensionDetails,
          }));
          setSelectedDimensionDetails(newDimensionDetails);
        }
      })
      .catch(() => {
        addNotification(
          MESSAGEBAR_TYPE.ERROR,
          "Failed to save Application settings"
        );
      })
      .finally(() => {
        scrollToTop();
      });
  };

  return (
    <article
      data-testid="hop-settings"
      className={mergeClasses(
        "d-flex amd-container-vcolumn",
        classes.containerHeight
      )}
    >
      {isLoading && (
        <Spinner
          labelPosition="below"
          label="Loading..."
          className={classes.containerHeight}
        />
      )}
      {error && (
        <div style={{ margin: "auto" }}>
          <NoDataCard message={EMPTY_STATE_MESSAGE.Data} />
        </div>
      )}
      {!error && !isLoading && (
        <>
          <DimensionTabs
            onDimensionTabChange={onDimensionTabChange}
            dimensionName={selectedDimensionTab}
            dimensionTabList={hopSettingsTabs}
          />
          {selectedDimensionDetails && (
            <DimensionDetails
              onChangeThresholdValue={onChangeThresholdValue}
              onSaveDimension={onSaveDimension}
              selectedDimensionDetails={selectedDimensionDetails}
              onCancelDimension={onCancelDimension}
            />
          )}
        </>
      )}
    </article>
  );
};

export default HopSettings;

const useStyles = makeStyles({
  containerHeight: {
    minHeight: "518px",
  },
});
